import { OptionLike } from "helpers/OptionHelper";
import { ExemptionMotive } from "models/ExemptionMotive";
import { ExemptionMotiveType } from "models/ExemptionMotiveType";
import { TagCompany } from "models/TagCompany";
import { TollStation } from "models/TollStation";
import { AnalysisStatusCodeRequest, ApplicationEventType, ApplicationStatus, TollStationProps } from "models/types";
import { VehicleCategory } from "models/VehicleCategory";

export interface BaseListResponse<T = any> {
	empty: boolean;
	first: boolean;
	last: boolean;
	content: T[];
	totalPages: number;
	totalElements: number;
	size: number;
	pageable?: {
		offset: number;
		pageNumber: number;
		pageSize: number;
		paged: boolean;
	};
}

export interface AddressDataResponse {
	cep: string;
	logradouro: string;
	complemento: string;
	unidade: string;
	bairro: string;
	localidade: string;
	uf: string;
	estado: string;
	regiao: string;
	ibge: string;
	gia: string;
	ddd: string;
	siafi: string;
	erro?: boolean;
}

export interface RegisterEventRequest {
	event: {
		type: keyof typeof ApplicationEventType;
		title?: string;
		description?: string;
	};
	documents?: DocumentLike[];
	documentFolders?: AnalysisReplyDocumentFolder[];
	analysisStatusCode?: keyof typeof AnalysisStatusCodeRequest;
	approvalData?: {
		exemptionMotive: string;
		exemptionMotiveType: string;
		propertyCode?: string;
		exemptionInitDatetime: string;
		exemptionEndDatetime: string;
		tollStations: string[];
		vehicleCategory: string;
		tollBoothType: string;
		tagNumber: string;
		tagCompany: TagCompany;
	};
	cancellationReasonId?: string;
	cancellationOtherText?: string;
	cancellationDate?: string;
}

export interface DocumentLike {
	blobName: string;
	name: string;
	error?: string;
}

export interface BaseDocumentFolder {
	id?: string;
	exemptionDocumentFolderType: string;
	documents: DocumentLike[];
}

export interface AnalysisEventDocument extends DocumentLike {
	id: string;
	exemptionDocumentFolderType: string | null;
	applicationDocumentFolder: string | null;
	exemptionDocumentFolderTypeName: string | null;
}

export interface AnalysisReplyDocumentFolder extends BaseDocumentFolder {
	documents: AnalysisEventDocument[];
}

export interface AnalysisEventDocumentFolder extends BaseDocumentFolder {
	id?: string;
	exemptionDocumentFolderType: string;
	documents: AnalysisEventDocument[];
	folderTypeData: DocumentFolderType;
}

export interface AnalysisEventData {
	id: string;
	type: ApplicationEventType;
	title: string;
	description: string;
	createdByGroup: string;
	createdAt: string;
	tollIsentos?: TollStationProps[];
}

export enum AnalysisStatusLabel {
	EM_ANDAMENTO = "Em andamento",
	APROVADO = "Aprovado",
	SOLICITADA_ALTERACAO = "Solicitada alteração",
	REPROVADO = "Reprovado",
	CANCELADO = "Cancelado"
}

export enum RedirectApplicationStatusLabel {
	APROVADO = "Aprovado",
	SOLICITADA_ALTERACAO = "Solicitada alteração",
	REPROVADO = "Reprovado",
	DEVOLVIDO = "Devolvido"
}

export interface ApplicationAnalysisData {
	id: string;
	applicationProcessCode: string;
	applicationStatus: ApplicationStatus;
	analysisCode: string;
	analysisStartDatetime: string;
	analysisEndDatetime: string | null;
	status: AnalysisStatusLabel;
	events: AnalysisEventData[];
}

export interface DocumentFolderType {
	id: string;
	requestedAt: string;
	alwaysRequired: boolean;
	minAmountDocuments: number;
	expectedFormatsText: string;
	requiredDocumentTypeGroup: string;
	documentType: {
		id: number;
		name: string;
		details: string;
		isValidatedByApi: boolean;
	};
	documentTemplates: {
		id: number;
		blobName: string;
		description: string;
	}[];
}

export interface FileExtensionData {
	extension: string;
	label: string;
}

export interface DocumentFolderTypesAndFileExtensions {
	documentFolderTypes: DocumentFolderType[];
	fileExtensions: FileExtensionData[];
}

export interface ApplicationFormDocument extends DocumentLike {
	id?: string;
}

export interface ZipDownloadRequest {
	containerName: string;
	applicationProcessCode: string;
	downloadedFolders: ZipDownloadedFolderRequest[];
}

export interface ZipDownloadedFolderRequest {
	folderName: string;
	fileNames: string[];
}

export interface ApplicationFormDocumentFolder extends BaseDocumentFolder {
	id?: string;
	exemptionDocumentFolderType: string;
	documentTypeName?: string;
	documents: ApplicationFormDocument[];
}

export interface ApplicationDocumentFolder {
	id: string;
	version: number;
	status: ApplicationDocumentFolderStatus;
	createdAt: string;
	exemptionDocumentFolderType: ExemptionDocumentFolderType;
	documents: ApplicationDocument[];
	documentTypeName: string;
}

export interface ApplicationDocument {
	id: string;
	name: string;
	blobName: string;
}

export enum ApplicationDocumentFolderStatus {
	SUBMITTED = "SUBMITTED",
	REJECTED = "REJECTED",
	REQUESTED = "REQUESTED",
	PENDING = "PENDING"
}

export interface DocumentType {
	id: string;
	name: string;
	details: string;
}
export interface ExemptionDocumentFolderType {
	id: string;
	documentType: DocumentType;
	documentTypeId: string;
}

export enum DocumentRequestedMoment {
	AT_START = "AT_START",
	ON_GOING = "ON_GOING"
}
export interface Paginated<I> {
	content: I[];
	totalPages: number;
}

export interface ApplicationFilterOptions {
	statuses: string[];
	analysisStatuses: OptionLike[];
	groups: OptionLike[];
	exemptionTypes: string[];
	concessionaires: OptionLike[];
}

export interface ApplicationApprovalData {
	id: string;
	exemptionMotive: ExemptionMotive;
	exemptionMotiveType: ExemptionMotiveType;
	propertyCode: string;
	tollBoothType: string;
	exemptionInitDatetime: string;
	exemptionEndDatetime: string;
	tollStation: TollStation[];
	vehicleCategory: VehicleCategory;
}

export interface CancellationReason {
	id: string;
	name: string;
}
